// TelcoTrack — VBř P0: verzovaný podpisový profil (plná moc, limit, typ smlouvy).
// Bezúplatnost ≠ oprávnění podepsat.

import { BREMENA_SELF_SIGN_MAX_CZK } from '../constants/bremena.constants';
import type { BremenaRepresentativeId } from './templates/template-catalog.types';

export interface BremenaSigningPoa {
  holder: 'protelco' | 'cetin' | 'other';
  principal: 'cetin' | 'investor' | 'other';
  valid: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  /** Instrument / contract types covered by the mandate. */
  mandateScope: string[];
  coversGratuitous: boolean;
}

export type BremenaNotarizationRule = 'required' | 'none' | 'return_copies_only';
export type BremenaSignatureOrder = 'owner_first' | 'operator_first';
export type BremenaEnvelopeSize = 'C4' | 'C5';

export interface BremenaSigningProfile {
  id: string;
  version: number;
  investorKey: string;
  /** Display names live here — not hardcoded in generate. */
  belowLimitSigner: { representative: BremenaRepresentativeId; name: string; org: string };
  aboveLimitSigner: { representative: BremenaRepresentativeId; name: string; org: string };
  selfSignLimitCzk: number;
  poa: BremenaSigningPoa;
  /** How the limit is computed (total compensation CZK). */
  limitCalculation: 'total_compensation_czk';
  /** Paper originals by instrument family (clause + postal pack). */
  originalsCountByInstrument?: {
    sosb?: number;
    sk_konecna_zapsana?: number;
    sk_konecna_nezapsana?: number;
    suz?: number;
  };
  signatureOrder?: BremenaSignatureOrder;
  notarization?: BremenaNotarizationRule;
  returnCopies?: number;
  envelopeSize?: BremenaEnvelopeSize;
}

export const BREMENA_DEFAULT_SIGNING_PROFILE: BremenaSigningProfile = {
  id: 'cetin-vbremena-v1',
  version: 1,
  investorKey: 'cetin',
  belowLimitSigner: {
    representative: 'protelco',
    name: 'Marek Smutný',
    org: 'ProTelco s.r.o.',
  },
  aboveLimitSigner: {
    representative: 'cetin',
    name: 'Pavel Polzer',
    org: 'CETIN a.s.',
  },
  selfSignLimitCzk: BREMENA_SELF_SIGN_MAX_CZK,
  poa: {
    holder: 'protelco',
    principal: 'cetin',
    valid: true,
    mandateScope: ['sosb', 'sk_konecna', 'suz', 'zrizeni_sluzebnosti'],
    coversGratuitous: false,
  },
  limitCalculation: 'total_compensation_czk',
  originalsCountByInstrument: {
    sosb: 2,
    sk_konecna_zapsana: 3,
    sk_konecna_nezapsana: 2,
    suz: 2,
  },
  signatureOrder: 'owner_first',
  notarization: 'return_copies_only',
  returnCopies: 1,
  envelopeSize: 'C4',
};

/** Stejnopisy from signing profile (SK zapsaná 3, jinak 2 — unless the profile overrides). */
export function resolveOriginalsCount(
  instrumentType: string | null | undefined,
  opts?: { zapsanoVKn?: boolean | null; profile?: BremenaSigningProfile | null },
): number {
  const profile = opts?.profile ?? BREMENA_DEFAULT_SIGNING_PROFILE;
  const counts = profile.originalsCountByInstrument ?? BREMENA_DEFAULT_SIGNING_PROFILE.originalsCountByInstrument;
  const type = (instrumentType ?? '').trim();
  if (type === 'sk_konecna' || type === 'sk') {
    return opts?.zapsanoVKn
      ? counts?.sk_konecna_zapsana ?? 3
      : counts?.sk_konecna_nezapsana ?? 2;
  }
  if (type === 'suz') return counts?.suz ?? 2;
  return counts?.sosb ?? 2;
}

export interface BremenaSigningResolution {
  representative: BremenaRepresentativeId;
  signerName: string;
  signerOrg: string;
  /** False when POA/limit/scope does not authorize this signature. */
  signingAuthorityOk: boolean;
  reasons: string[];
  profileId: string;
  profileVersion: number;
}

function inScope(profile: BremenaSigningProfile, contractType?: string | null): boolean {
  const typ = (contractType ?? '').trim();
  if (!typ) return true;
  return profile.poa.mandateScope.includes(typ);
}

/**
 * Who appears on the template vs whether they may sign.
 * Gratuitous / 0 Kč never grants signing authority by itself.
 */
export function resolveBremenaSigningFromProfile(
  totalCzk: number | null | undefined,
  opts?: {
    profile?: BremenaSigningProfile | null;
    explicit?: BremenaRepresentativeId | string | null;
    contractType?: string | null;
    gratuitous?: boolean;
  },
): BremenaSigningResolution {
  const profile = opts?.profile ?? BREMENA_DEFAULT_SIGNING_PROFILE;
  const explicit = (opts?.explicit ?? '').trim();
  const reasons: string[] = [];

  if (explicit === 'nova_optika') {
    return {
      representative: 'nova_optika',
      signerName: 'Nova Optika',
      signerOrg: 'Nova Optika s.r.o.',
      signingAuthorityOk: true,
      reasons: ['Explicitní zástupce Nova Optika.'],
      profileId: profile.id,
      profileVersion: profile.version,
    };
  }

  const overLimit =
    totalCzk != null && Number.isFinite(totalCzk) && totalCzk > profile.selfSignLimitCzk;
  const slot = overLimit ? profile.aboveLimitSigner : profile.belowLimitSigner;
  if (overLimit) {
    reasons.push(
      `Náhrada ${totalCzk} Kč > limit ${profile.selfSignLimitCzk} Kč — podpis ${slot.name} / ${slot.org}.`,
    );
  } else {
    reasons.push(
      `Náhrada ${totalCzk ?? 'neznámá'} Kč ≤ limit ${profile.selfSignLimitCzk} Kč (výpočet: ${profile.limitCalculation}) — návrh ${slot.name}.`,
    );
  }

  let signingAuthorityOk = profile.poa.valid && inScope(profile, opts?.contractType);
  if (!profile.poa.valid) {
    reasons.push('Plná moc v profilu není platná.');
  }
  if (opts?.contractType && !inScope(profile, opts.contractType)) {
    reasons.push(`Typ smlouvy ${opts.contractType} mimo rozsah mandátu.`);
    signingAuthorityOk = false;
  }
  const gratuitous = opts?.gratuitous === true || totalCzk === 0;
  if (gratuitous) {
    reasons.push('Bezúplatnost sama o sobě nezakládá oprávnění podepsat.');
    if (!profile.poa.coversGratuitous) {
      signingAuthorityOk = false;
    }
  }

  return {
    representative: slot.representative,
    signerName: slot.name,
    signerOrg: slot.org,
    signingAuthorityOk,
    reasons,
    profileId: profile.id,
    profileVersion: profile.version,
  };
}

export function bremenaSigningProfileLabelCs(resolution: BremenaSigningResolution): string {
  return `${resolution.signerName}, ${resolution.signerOrg}`;
}
